import type { PublicSurvey } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Loading, Panel, PanelHeader, TextAreaField, cx, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import { RichText } from "../components/rich-text";

/** The customer's "How did we do?" page (from the job-closed text). */
export function PublicSurveyPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["survey", token], queryFn: () => get<PublicSurvey>(`/api/public/surveys/${token}`), retry: false });
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const send = useMutation({ mutationFn: () => post<PublicSurvey>(`/api/public/surveys/${token}`, { score, comment }), onSuccess: (d) => qc.setQueryData(["survey", token], d) });
  useEffect(() => {
    if (q.data) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data]);
  if (q.isLoading)
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert>
      </div>
    );
  const s = q.data;
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-4">
          {s.company.logoUrl && <img src={s.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
          <span className="text-lg font-semibold">{s.company.name}</span>
        </div>
      </header>
      <main className="mx-auto flex max-w-xl flex-col gap-5 px-4 py-8">
        {s.answered ? (
          <>
            <h1 className="text-2xl font-semibold">Thank you, {s.customerFirstName}!</h1>
            <p className="text-ink-soft">{s.score !== null && s.score <= 3 ? "We're sorry it wasn't better. Someone from our office will call you soon." : "We're glad we could help."}</p>
            {s.reviewUrl && (
              <a href={s.reviewUrl} target="_blank" rel="noreferrer" className="self-start rounded-[var(--radius-control)] bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-deep">
                Leave us a Google review
              </a>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">How did we do, {s.customerFirstName}?</h1>
            <div role="radiogroup" aria-label="Your rating" className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} role="radio" aria-checked={score === n} aria-label={`${n} of 5`} onClick={() => setScore(n)} className={cx("text-4xl leading-none transition-transform hover:scale-110", n <= score ? "text-amber-400" : "text-line")}>
                  ★
                </button>
              ))}
            </div>
            <TextAreaField label="Anything you'd like us to know? (optional)" rows={4} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
            {send.error && <Alert>{errorMessage(send.error)}</Alert>}
            <div>
              <Button variant="primary" busy={send.isPending} disabled={!score} onClick={() => send.mutate()}>
                Send
              </Button>
            </div>
            <p className="text-sm text-muted">Questions? Call {s.company.phone}.</p>
          </>
        )}
      </main>
    </div>
  );
}

/** On a job's overview: the survey answer, and sending the Google review link by hand. */
export function JobFeedback({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const q = useQuery({ queryKey: ["surveys", jobId], queryFn: () => get<{ id: string; score: number | null; comment: string; answeredAt: string | null; sentAt: string }[]>(`/api/jobs/${jobId}/surveys`) });
  const [sent, setSent] = useState(false);
  const send = useMutation({ mutationFn: () => post(`/api/jobs/${jobId}/review-link`), onSuccess: () => setSent(true) });
  const s = q.data?.[0];
  if (!s && !can("comms.send")) return null;
  return (
    <Panel>
      <PanelHeader
        title="Customer feedback"
        action={
          can("comms.send") && (
            <Button variant="secondary" className="px-3 py-1.5" busy={send.isPending} disabled={sent} onClick={() => send.mutate()}>
              {sent ? "Review link sent" : "Text Google review link"}
            </Button>
          )
        }
      />
      <div className="px-5 py-4 text-sm">
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        {!s ? (
          <span className="text-muted">No survey sent. It goes out when the job closes, if the "How did we do?" automation is on.</span>
        ) : s.answeredAt ? (
          <div>
            <span className="text-lg text-amber-400">{"★".repeat(s.score ?? 0)}</span>
            <span className="text-line">{"★".repeat(5 - (s.score ?? 0))}</span> <span className="font-semibold">{s.score} of 5</span>
            {s.comment && <RichText className="mt-1 text-ink-soft" text={s.comment} />}
          </div>
        ) : (
          <span className="text-muted">Survey sent {formatDateTime(s.sentAt)}; no answer yet.</span>
        )}
      </div>
    </Panel>
  );
}
